import { each } from 'lodash-es';

export class CacheModel {
    public persistent: boolean = false;
    public prefix: string = null;
    public expiration: number; // seconds, ignored if 0

    private data: object = {};
    private store: Storage = window.localStorage;

    constructor(expiration = 0) {
        this.expiration = expiration;
    }

    public has(key) {
        if (!key) {
            return false;
        }

        if (this.expiration > 0 && key.indexOf('-expiration') > -1) { return true; }

        const keyExists = (this.persistent && this.store) ?
            (this.store.getItem(this.prefix + '-' + key) != null) : Object.prototype.hasOwnProperty.call(this.data, key);

        if (keyExists && this.expiration == 0) { return true; }
        return (keyExists && Date.now() < Number(this.get(key + '-expiration')));
    }

    public get(key) {
        if (!key) {
            return null;
        }

        if (this.has(key)) {
            if (this.persistent && this.store) {
                const value = this.store.getItem(this.prefix + '-' + key);
                const first = value.substr(0, 1);
                return (typeof value === 'string' && (first === '{' || first === '[')) ? JSON.parse(value) : value;
            }

            return this.data[key];
        }

        return null;
    }

    public getItems(key) {
        return this.getItemArray(key);
    }

    public getItemArray(key) {
        const items = [];
        each(this.getIDs(key), (id) => {
            if (this.has(key + '-' + id)) {
                items.push(this.get(key + '-' + id));
            }
        });

        return items;
    }

    public getItemAssoc(key) {
        const items = {};
        each(this.getIDs(key), (id) => {
            if (this.has(key + '-' + id)) {
                items[id] = this.get(key + '-' + id);
            }
        });

        return items;
    }

    public set(key, value) {
        if (!key) {
            return;
        }

        if (this.persistent && this.store) {
            if (typeof value == 'object') {
                value = JSON.stringify(value);
            }

            this.store.setItem(this.prefix + '-' + key, value);
            if (this.expiration > 0) {
                this.store.setItem(this.prefix + '-' + key + '-expiration', String(Date.now() + (this.expiration * 1000)));
            }
        } else {
            this.data[key] = value;
            if (this.expiration > 0) {
                this.data[key + '-expiration'] = Date.now() + (this.expiration * 1000);
            }
        }
    }

    public setItems(key, items) {
        const ids = this.has(key + 'IDs') ? this.get(key + 'IDs') : [];

        each(items, (item, i) => {
            const id = (Object.prototype.hasOwnProperty.call(item, 'id')) ? item.id : i;
            if (ids.indexOf(id) == -1) { ids.push(id); }
            this.set(key + '-' + id, item);
        });

        this.set(key, true);
        this.set(key + 'IDs', ids);
    }

    public delete(key) {
        if (this.persistent && this.store) {
            this.store.removeItem(this.prefix + '-' + key);
        } else {
            delete this.data[key];
        }
    }

    public deleteItems(key) {
        each(this.getIDs(key), (id) => {
            this.delete(key + '-' + id);
            this.delete(key + '-' + id + '-expiration');
        });

        this.delete(key);
        this.delete(key + 'IDs');
        this.delete(key + '-expiration');
    }

    public clear() {
        if (this.persistent && this.store) {
            this.store.clear();
        } else {
            this.data = {};
        }
    }

    protected getIDs(key) {
        if (this.has(key + 'IDs')) {
            return this.get(key + 'IDs');
        }

        return [];
    }

    public getETag(key: string, ignoreCount: boolean = false) {
        let eTag: string = (this.has(key) && this.has(key + '-eTag')) ? this.get(key + '-eTag') : '';

        if (eTag && eTag.indexOf('-') > -1) {
            const cnt = Number(eTag.split('-')[1]);

            if (!ignoreCount && this.getItems(key).length != cnt) {
                eTag = '';
            }
        }

        return eTag;
    }

    public setETag(key, eTag) {
        this.set(key + '-eTag', eTag);
    }
}
